import React, {FC, useState} from "react";
import {StyledButton, StyledDiv, StyledTypography} from "../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {useConsumerSendMailMutation, usePublicPageConsumerCreateMutation} from "../../Queries";
import {isValidEmail} from "../../components/Validations";
import clsx from "clsx";
import {Theme} from "@mui/material";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {DropOffIcon, PackItIcon, TradeInIcon} from "../../svg/SvgIcons";
import {LocaleAtom, LocalizationsAtom} from "../../providers/localization/LocalizationProvider";
import {TradeInBox} from "./TradeInBox";
import {Images} from "../../Images";
import {AccessContextData} from "../../providers/access/AppContextProvider";
import {CountryMapFactory} from "../../utility/CountryMapFactory";
import {ReturnsPagePath} from "../restricted/returns/ReturnsPage";
import {LoginContext} from "../LoginContext";
import {useNavigate} from "react-router-dom";
import {Dialogs} from "../../Dialog";

export const PitayaPage: FC<any> = () => {
   const [loginType, setLoginType] = useState<'login' | 'wholesale' | undefined>();
   const [email, setEmail] = useState<string>('');
   const localizations = useRecoilValue(LocalizationsAtom);
   const appContext = useRecoilValue(AccessContextData);
   const [register] = usePublicPageConsumerCreateMutation()
   const [sendMail] = useConsumerSendMailMutation();
   const locale = useRecoilValue(LocaleAtom);
   const setLoginContext = useSetRecoilState(LoginContext)
   const navigate = useNavigate();

   const classes = useStyles({
      leftImage: localizations.frontPageImage1 || "url('https://pitaya.dk/cdn/shop/files/D96130DF-986D-4285-A084-90E43ED2C89A.jpg?v=1738356056&width=600')",
      rightImage: localizations.frontPageImage2 || "url('https://pitaya.dk/cdn/shop/files/IMG_7326.jpg?v=1755094046&width=600')",
   });

   let showLoginOptions = true;

   async function registerNewUser() {
      const countries = CountryMapFactory.getCountriesFromDomain(window.location.hostname)
      const country = countries?.[0]?.alpha2 || undefined

      const consumer = await register({
         variables: {
            shopId: appContext.shopId,
            locale: locale,
            country: window.Shopify?.country || window.tradeInSession?.countryCode || country,
            currency: appContext.currency || window.Shopify?.currency?.active || window.tradeInSession?.currency
         }
      });
      if (consumer.data?.ConsumerCreate?.accessToken) {
         setLoginContext({
            accessToken: consumer.data.ConsumerCreate.accessToken
         })
         navigate(ReturnsPagePath());
      }
   }

   const onStart = async () => {
      if (!loginType) {
         return;
      } else if (loginType === 'login') {
         // Handle email sending
         let shopId = window.shopId || window.brandId;
         if (!!email && isValidEmail(email)) {
            await sendMail({
               variables: {
                  shopId: shopId,
                  mail: email
               }
            });
            setLoginType(undefined)
            setEmail('');
            // Show popup
            Dialogs.info({
               title: localizations.mainPageMailSent,
               subtitle: localizations.mainPageMailSent2,
               okayText: 'OK'
            });
         }
      } else if (loginType === 'wholesale') {
         await registerNewUser();
      }
   }

   return <div>
      <StyledDiv type={"main"} className={classes.container}>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.leftImage)}>
            &nbsp;
         </StyledDiv>
         <StyledDiv type={"contrast"} className={classes.main}>
            <div className={classes.content}>
               <StyledTypography variant={"h1"} type={"contrast"} style={{marginBottom: '26px'}}>
                  {localizations.loginPageTitle}
               </StyledTypography>
               <StyledTypography variant={"body1"} type={"contrast"} style={{marginBottom: '20px'}}>
                  {localizations.loginPageDescription}<br/>
                  {localizations.loginPageDescription2}
               </StyledTypography>
               {showLoginOptions && <div className={classes.tradeInBoxContainer}>
                   <TradeInBox id={"login"} title={localizations.publicPageLoginTitle}
                               description={localizations.publicPageLoginDescription}
                               onClick={() => {
                                  const element = document.getElementById('loginemail');
                                  element?.removeAttribute('disabled');
                                  element?.focus();
                                  setLoginType("login");
                               }}
                               image={Images.TRADEIN_DTC}
                               selected={loginType === 'login'}
                               disableIcons={true}
                               sx={{}}
                   >
                       <form onSubmit={async (e) => {
                          e.preventDefault();
                          await onStart();
                       }}>
                           <input
                               style={{
                                  minWidth: "150px",
                                  width: '100%',
                                  backgroundColor: '#fff',
                                  pointerEvents: loginType !== 'login' ? 'none' : 'auto'
                               }}
                               className={"input"}
                               id="loginemail"
                               type="email"
                               value={email}
                               disabled={loginType !== 'login'}
                               onChange={(event) => {
                                  setEmail(event.target.value);
                               }}
                               placeholder={localizations.mainPageEnterEmail}
                               aria-haspopup={false}
                               autoComplete={"off"}
                               required
                               autoCorrect="off"
                               autoCapitalize="off"
                           />
                       </form>
                   </TradeInBox>
                   <TradeInBox id={"wholesale"} title={localizations.publicPageWholeSaleTitle}
                               description={localizations.publicPageWholeSaleDescription}
                               onClick={() => setLoginType("wholesale")}
                               disableIcons={true}
                               image={Images.TRADEIN_RETAILER}
                               selected={loginType === 'wholesale'}
                   />
               </div>}
               <div className={classes.buttonContainer}>
                  <StyledButton disabled={showLoginOptions && !loginType} styletype={"front"} className={classes.button}
                                onClick={onStart}>
                     {localizations.publicPagePageStartButton}
                  </StyledButton>
               </div>
            </div>
         </StyledDiv>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.rightImage)}>
            &nbsp;
         </StyledDiv>
      </StyledDiv>
      <RabensEasyBox/>
   </div>
}

type imageProps = {
   leftImage: string
   rightImage: string
}

const useStyles = makeStyles({
   title: {
      marginBottom: '20px'
   },
   tradeInBoxContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      marginTop: '20px'
   },
   buttonContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
   },
   button: {
      marginTop: '25px',
      width: '250px',
      marginBottom: '40px'
   },
   backContainer: {
      marginTop: '20px'
   },
   content: {
      maxWidth: '500px',
      textAlign: 'center'
   },
   container: {
      display: 'flex',
      width: '100%',
      flexDirection: 'row',
      alignItems: 'top',
      padding: '0 40px 0 40px'
   },
   accessForm: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
   },
   image: {
      width: '400px',
      minHeight: '496px',
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "top center",
      overflowX: 'hidden',
      overflowY: 'hidden',
   },
   main: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
      padding: '50px'
   },
   leftImage: {
      backgroundImage: (props: imageProps) => props.leftImage
   },
   rightImage: {
      backgroundImage: (props: imageProps) => props.rightImage
   },
   '@media only screen and (max-width: 800px)': {
      container: {
         flexDirection: 'column',
         padding: '0 23px 0 23px'
      } as any,
      image: {
         width: '100%',
         maxHeight: '200px',
         minHeight: '300px',
         backgroundPosition: "center",
      },
      buttonContainer: {
         alignItems: 'center',
         justifyContent: 'center'
      },
   },
   '@media only screen and (max-width: 1024px)': {
      container: {
         padding: '0 23px 0 23px'
      } as any
   },
})


const RabensEasyBox = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useEasyStyles();
   return <div className={classes.container}>
      <div>
         <StyledTypography variant={"h1"} className={classes.title}>
            {localizations.easyBoxTitle}
         </StyledTypography>
      </div>
      <div>
         <div className={classes.list}>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <TradeInIcon type={"main"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection1Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection1Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <PackItIcon type={"main"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection2Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection2Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <DropOffIcon type={"main"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection3Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection3Description}
                  </StyledTypography>
               </div>
            </div>
         </div>
      </div>
   </div>
}


const useEasyStyles = makeStyles((theme: Theme) => ({
      container: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center'
      },
      title: {
         marginTop: '40px'
      },
      text: {
         marginBottom: '15px'
      },
      list: {
         display: 'flex',
         flexDirection: 'row',
         justifyContent: 'center',
         marginTop: '40px'
      },
      listItem: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         marginBottom: '20px',
         gap: '10px'
      },
      listAvatar: {},
      listText: {
         marginTop: '0',
         textAlign: 'center',
         maxWidth: '400px',
         padding: '0 20px 0 20px'
      },
      '@media only screen and (max-width: 800px)': {
         list: {
            flexDirection: 'column',
         },
         listItem: {
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '600px',
            gap: '10px'
         },
      }
   }),
);
